import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Cog, Loader2 } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState("login");
  const [info, setInfo] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const forgotSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setInfo("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      setInfo(data.message || "Se l'email esiste, riceverai il link di reset.");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-[#0B0F17]">
      {/* Left visual */}
      <div className="hidden lg:flex w-1/2 relative overflow-hidden border-r border-slate-800">
        <img
          src="https://images.unsplash.com/photo-1565954786194-d22abeaac3ae?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
          alt="Officina meccanica"
          className="absolute inset-0 h-full w-full object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-[#0B0F17] via-[#0B0F17]/70 to-transparent" />
        <div className="relative z-10 flex flex-col justify-end p-12">
          <div className="h-12 w-12 rounded-xl bg-amber-500 flex items-center justify-center mb-6">
            <Cog className="h-7 w-7 text-slate-950" />
          </div>
          <h1 className="font-heading text-4xl font-extrabold tracking-tight max-w-md">
            Tracciamento montaggi di parti meccaniche
          </h1>
          <p className="text-slate-400 mt-4 max-w-md text-sm leading-relaxed">
            Storico completo, tempi di montaggio e programmazione automatica delle commesse
            successive sulla stessa linea produttiva.
          </p>
        </div>
      </div>

      {/* Right form */}
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-sm animate-fade-up">
          <div className="lg:hidden flex items-center gap-2.5 mb-8">
            <div className="h-10 w-10 rounded-lg bg-amber-500 flex items-center justify-center">
              <Cog className="h-6 w-6 text-slate-950" />
            </div>
            <span className="font-heading font-extrabold text-xl">MEKANIKA</span>
          </div>
          <h2 className="font-heading text-2xl font-bold">
            {mode === "login" ? "Accedi al gestionale" : "Recupera credenziali"}
          </h2>
          <p className="text-slate-500 text-sm mt-1 mb-8">
            {mode === "login"
              ? "Inserisci le tue credenziali operatore."
              : "Inserisci la tua email: riceverai un link per reimpostare la password."}
          </p>

          {mode === "login" ? (
            <form onSubmit={submit} className="space-y-5">
              <div>
                <label className="text-xs font-mono-data uppercase tracking-widest text-slate-400">Email</label>
                <input
                  type="email"
                  data-testid="login-email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="mt-2 w-full h-11 rounded-lg bg-slate-900 border border-slate-700 px-4 text-sm outline-none focus:border-amber-500 transition-colors"
                  placeholder="nome@azienda.it"
                />
              </div>
              <div>
                <label className="text-xs font-mono-data uppercase tracking-widest text-slate-400">Password</label>
                <input
                  type="password"
                  data-testid="login-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="mt-2 w-full h-11 rounded-lg bg-slate-900 border border-slate-700 px-4 text-sm outline-none focus:border-amber-500 transition-colors"
                  placeholder="••••••••"
                />
              </div>
              {error && (
                <div data-testid="login-error" className="text-sm text-rose-400 bg-rose-500/10 border border-rose-500/30 rounded-lg px-4 py-2.5">
                  {error}
                </div>
              )}
              <button
                type="submit"
                disabled={loading}
                data-testid="login-submit"
                className="w-full h-11 rounded-lg bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold text-sm transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                Entra
              </button>
              <button
                type="button"
                data-testid="forgot-password-link"
                onClick={() => { setMode("forgot"); setError(""); setInfo(""); }}
                className="w-full text-sm text-slate-400 hover:text-amber-400 transition-colors"
              >
                Password dimenticata?
              </button>
            </form>
          ) : (
            <form onSubmit={forgotSubmit} className="space-y-5">
              <div>
                <label className="text-xs font-mono-data uppercase tracking-widest text-slate-400">Email</label>
                <input
                  type="email"
                  data-testid="forgot-email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="mt-2 w-full h-11 rounded-lg bg-slate-900 border border-slate-700 px-4 text-sm outline-none focus:border-amber-500 transition-colors"
                  placeholder="nome@azienda.it"
                />
              </div>
              {info && (
                <div data-testid="forgot-info" className="text-sm text-emerald-400 bg-emerald-500/10 border border-emerald-500/30 rounded-lg px-4 py-2.5">
                  {info}
                </div>
              )}
              {error && (
                <div data-testid="forgot-error" className="text-sm text-rose-400 bg-rose-500/10 border border-rose-500/30 rounded-lg px-4 py-2.5">
                  {error}
                </div>
              )}
              <button
                type="submit"
                disabled={loading}
                data-testid="forgot-submit"
                className="w-full h-11 rounded-lg bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold text-sm transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                Invia link di reset
              </button>
              <button
                type="button"
                onClick={() => { setMode("login"); setError(""); setInfo(""); }}
                className="w-full text-sm text-slate-400 hover:text-amber-400 transition-colors"
              >
                ← Torna al login
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
